import { parseCELQuery, parseGDPQuery } from '@rcg/query-parser';
import sizes from 'styles/sizes';
import { CABIN_AQUA, CABIN_BALCONY, CABIN_CONCIERGE, CABIN_DELUXE, CABIN_INTERIOR, CABIN_OUTSIDE } from '../../lib/cruise/sailings/Constants';

// COMMON HOVER UI SETTINGS
const getCommonHoverDefault = ({ color, tone }) => ({
	common: {
		buttons: {
			hover: {
				default: {
					color: 'primary',
					tone: 'main',
				},
				onHover: {
					color,
					tone,
				},
			},
		},
	},
});

// BRANDS
export const BRAND_RCI = {
	code: 'R',
	alpha3Code: 'rci',
	name: 'Royal Caribbean',
	nameOneWord: 'royal',
	g11n: true,
	categories: [CABIN_INTERIOR, CABIN_OUTSIDE, CABIN_BALCONY, CABIN_DELUXE],
	parseQuery: parseGDPQuery,
	itineraryMapPath: '/royal/cruise-search/itinerary-maps/desktop/',
	favicon: '/content/dam/royal/resources/icons/logo/favicon.ico',
	defaultOverviewImagePath: '/content/dam/royal/data/regions/itinerary-default-image.jpg',
	bazaarVoicePath: 'royalcaribbean',
	uiSettings: {
		...getCommonHoverDefault({ color: 'secondary', tone: 'main' }),
		cruiseCard: {
			actions: {
				focus: {
					color: 'primary',
					tone: 'main',
				},
				hover: {
					color: 'primary',
					tone: 'main',
				},
			},
			userIcons: {
				position: 'right', // left, right
				favorite: {
					position: 'center',
				},
			},
			ship: {
				textTransform: 'none',
			},
			portsHeader: {
				variant: 'label2Bold',
			},
			price: {
				position: 'right', // center, right
			},
			viewDatesCta: {
				uppercase: 'false',
			},
		},
		cruiseDetailsPanel: {
			size: 'medium',
			header: {
				color: 'primary',
			},
			tabs: {
				variant: 'basic',
			},
		},
		dateFilter: {
			toggle: {
				borderRadius: '10px',
			},
			dateRange: {
				dayOfWeek: {
					color: 'grey',
					shade: '700',
				},
				dayLabel: {
					color: 'primary',
					shade: 'dark',
				},
				dayButton: {
					color: 'primary',
					shade: 'dark',
					backgroundColor: 'grey',
					backgroundShade: '100',
					borderRadius: '5px',
					height: '100%',
				},
			},
		},
		loadMoreButton: {
			uppercase: 'false',
			variant: 'contained',
			color: 'accent',
			size: 'large',
		},
		filtersModal: {
			showResultsButton: {
				uppercase: 'false',
				variant: 'contained',
				color: 'primary',
				size: 'large',
				highlighted: false,
			},
			filtersGrid: {
				gap: '10px',
			},
			monthsButton: {
				maxWidth: '68px',
			},
			monthsGrid: {
				gap: '6px 4px',
			},
		},
		nightsFilter: {
			title: {
				variant: 'old',
			},
		},
		resultsHeader: {
			title: 'font-size: 20px;',
			labels: `
        font-size: 16px;
        letter-spacing: 0.02em;
        line-height: 120%;
      `,
		},
	},
};

export const BRAND_CEL = {
	code: 'C',
	alpha3Code: 'cel',
	name: 'Celebrity',
	nameOneWord: 'celebrity',
	g11n: true,
	categories: [CABIN_INTERIOR, CABIN_OUTSIDE, CABIN_BALCONY, CABIN_CONCIERGE, CABIN_AQUA, CABIN_DELUXE],
	parseQuery: parseCELQuery,
	favicon: '/favicon.ico',
	itineraryMapPath: '/content/dam/celebrity/itinerary-maps/1200x430/',
	defaultOverviewImagePath: '/content/dam/celebrity/new-images/CEL_Ship_Wake.jpg',
	bazaarVoicePath: 'celebritycruises',
	uiSettings: {
		...getCommonHoverDefault({ color: 'primary', tone: 'light' }),
		cruiseCard: {
			actions: {
				focus: {
					color: 'primary',
					tone: 'dark',
				},
				hover: {
					color: 'primary',
					tone: 'light',
				},
			},
			userIcons: {
				position: 'left', // left, right
				favorite: {
					position: 'right',
				},
			},
			ship: {
				textTransform: 'uppercase',
			},
			portsHeader: {
				variant: 'label2SemiBold',
			},
			price: {
				position: 'center', // center, right
			},
			viewDatesCta: {
				uppercase: true,
			},
		},
		cruiseDetailsPanel: {
			size: 'large',
			header: {
				color: 'secondary',
			},
			tabs: {
				variant: 'scrollable', // change to scrollable
			},
		},
		loadMoreButton: {
			uppercase: true,
			variant: 'outlined',
			color: 'secondary',
			size: 'medium',
		},
		dateFilter: {
			toggle: {
				borderRadius: '0px',
			},
			dateRange: {
				dayOfWeek: {
					color: 'common',
					shade: 'black',
				},
				dayLabel: {
					color: 'common',
					shade: 'black',
				},
				dayButton: {
					color: 'primary',
					shade: 'contrastText',
					backgroundColor: 'primary',
					backgroundShade: 'dark',
					borderRadius: '0px',
					height: '26px',
				},
			},
		},
		filtersModal: {
			showResultsButton: {
				variant: 'outlined',
				uppercase: true,
				color: 'secondary',
				size: 'medium',
				highlighted: true,
			},
			filtersGrid: {
				gap: '20px 8px',
			},
			monthsButton: {
				maxWidth: '64px',
			},
			monthsGrid: {
				gap: '8px',
			},
		},
		nightsFilter: {
			title: {
				variant: 'title4',
			},
		},
		resultsHeader: {
			title: `
        font-size: 18px;
        @media (min-width: ${sizes.media.mediumMinWidth}px) {
          font-size: 22px;
        }
      `,
			labels: `
        font-size: 14px;
        letter-spacing: 0.02em;
        line-height: 125%;
      `,
		},
	},
};

export const BRANDS = [BRAND_RCI, BRAND_CEL];
/**
 * Query Params
 */
export const SEARCH = 'search';
export const SORT = 'sort';
export const QUALIFIERS = 'qualifiers';
export const PAGINATION = 'pagination';
export const FACET = 'facet';
export const GROUP_ID = 'itineraryPanel';
export const ANALYTICS = 'analytics';
export const PROMO = 'promo';
export const FARE = 'fare';
export const COUPON = 'coupon';
export const POPUP = 'popup';
export const CROWNANDANCHORNUMBER = 'crownAndAnchorNumber';
export const NLS_PARAM = 'q';
export const PARAM_VALUE_DELIMITER = '|';

export const LOYALTY_ID = {
	urlParam: 'loyaltyId',
	cookie: 'loyalty_ID',
	bookingParam: 'loyaltyNumber',
	domain: '.royalcaribbean.com',
};
export const EMAIL_ID = {
	urlParam: 'cee',
	cookie: 'email_ID',
	bookingParam: 'cee',
	domain: '.royalcaribbean.com',
};
export const PROMO_CODE = 'promoCode';
export const COUNTRY = 'country';

/**
 * Search filter options
 */
export const ALL = 'all';
export const DESTINATION = 'destination';
export const UNIQUE_DESTINATION = 'uniqueDestination';
export const NIGHTS = 'nights';
export const SHIP = 'ship';
export const START_DATE = 'startDate';
export const DATE_RANGE = 'dateRange';
export const PORT = 'port';
export const DEPARTURE_PORT = 'departurePort';
export const CABIN_CLASS = 'cabinClass';
export const OCCUPANCY = 'occupancy';
export const ADULTS = 'adults';
export const CHILDREN = 'children';
export const CUSTOM = 'custom';
export const VOYAGE_TYPE = 'voyageType';

/* Occupancy */
export const OCCUPANCY_RANGE = {
	min: 1,
	max: 4,
	adults: { min: 1, max: 4 },
	children: { min: 0, max: 3 },
	infants: { min: 0, max: 3 },
};

export const INCREMENT = 'max';
export const DECREMENT = 'min';

export const ADULTS_DEFAULT = 2;
export const CHILDREN_DEFAULT = 0;

/* Containers filter */
export const CONTAINERS = {
	FILTER_DESTINATION: 'filter_destination',
	FILTER_MY_OFFERS: 'filter_my_offers',
	FILTER_CUSTOM: 'filter_custom',
};

/**
 * Cabin Class default
 */
export const INTERIOR = 'INTERIOR';

export const searchFilterOptions = {
	[DESTINATION]: [],
	[NIGHTS]: [],
	[SHIP]: [],
	[START_DATE]: [],
	[PORT]: [],
	[ADULTS]: [],
	[CHILDREN]: [],
	[CUSTOM]: [],
};

/**
 * Localization
 */
export const DEFAULT_LANGUAGE = 'en';
// export const DEFAULT_LANGUAGE = 'fr';
// export const DEFAULT_LANGUAGE = 'es';
export const DEFAULT_MARKET = 'USA';
// export const DEFAULT_MARKET = 'fra';
// export const DEFAULT_MARKET = 'usa';

export const searchDefault = {
	// [CABIN_CLASS]: [INTERIOR],
};

export const blankResults = {
	cruises: [],
	total: 0,
	openSearch: true,
};

export const PORT_CODE_PREFIX = 'portCode_';
export const DAY_TYPE_TOUR = 'TOUR';
export const PORT_VISIT_ACTIVITY_TOURING = 'TOURING';
export const CRUISING_PORT_CODES = ['ASE', 'CRU', 'XID'];
export const LABEL_MISSING_PORT_CODE = 'MISSING PORT CONTENT';
export const FILTER_MODE = {
	ALL_FILTERS: 'allFilters',
	SINGLE_FILTER: 'singleFilter',
};

// TARGETED OFFERS
export const TARGETED_OFFERS_SERVICE_ERROR_MESSAGES = {
	invalidLoyaltyId: 'ERR_004',
	badRequest: 'ERR_002',
};

export const CODES_ALLOW_RETRY = {
	internalESL: 'ERR_003',
	unhandledError: 'ERR_999',
	connectingESL: 'ERR_101',
	missingInfoESLRequest: 'ERR_201',
	missingInfoESLResponse: 'ERR_302',
	INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
};

export const CALL_ORIGIN_TARGETED_OFFERS = {
	URL: 'URL',
	COOKIE: 'COOKIE',
	FORM: 'SIGN_IN',
};

// COOKIES
export const ADOBE_SEGMENT = 'aam_sc';

// LOCAL STORAGE
export const TARGETED_OFFER_APPLIED = {
	key: 'TOapplied',
	status_active: 'active',
	status_removed: 'removed',
};

export const NUMBER_OF_GUESTS = {
	key: 'NumberOfGuests',
	adults: 'adults',
	children: 'children',
};

// CLASSES
export const ENHANCEMENT_CLASS = 'enhancement-design';
export const BASICDATA_FIXED_DOWN_CLASS = 'basicData--fixedDown';
export const BASICDATA_FIXED_TOP_CLASS = 'basicData--fixedTop';

// FAVORITES & WATCHLIST
export const FAVORITES_AND_WATCHLIST_STATES = {
	SELECTED: 'SELECTED',
	UNSELECTED: 'UNSELECTED',
};

export const SIGN_IN = 'SIGN IN';

export const URL_IMG_CAS = '/content/dam/royal/resources/icons/cas-tiers/{{loyaltyLevel}}.svg';

export const CAS_IMG_DEFAULT = 'pre-gold';

export const MESSAGE_LOGGED = 'Hi, {{firstName}}';

// SORT
export const SORT_BY_PRICE = 'PRICE';
export const SORT_ORDER_ASC = 'ASC';

// INDEXING
export const ENABLE_PAGE_INDEXING = 'index, follow';
export const DISABLE_PAGE_INDEXING = 'noindex, nofollow';

// TYPOGRAHY
export const label2SemiBold = 'label2SemiBold';
export const label3SemiBold = 'label3SemiBold';

export const MULTI_QUERY_PARAM_DIVIDER = '||';
export const QUERY_PARAM_DIVIDER = '|';
export const QUERY_PARAM_ALLOCATOR = ':';

export const MAX_MEDALLIA_SDK_CHECK_ATTEMPTS = 20;

export const COMPARE_PAGE = 'compare';
export const CRUISES_COMPARE_PAGE = 'cruises/compare';

export const SAILDATE = 'SAILDATE';

export const EVENT_SEARCH_RESULTS_READY = 'onSearchResultsReady';
export const EVENT_CLEAN_WIDGET_FILTERS = 'onCleanWidgetFilters';

export const HOME_PAGE_QUERY_PARAM = 'hp_search_widget';
